import { ARCHIVED_BANNER_HEIGHT } from "components/archived-project-banner";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button } from "antd";
import { message } from "utils/ciq-message";
import { GridLoadingIndicator } from "components/widgets";

import {
  ProjectContext,
  isPermissionNotGrantted,
  TProjectContext
} from "context/ProjectProvider";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useMutation } from "@apollo/client";
import InviteProjectCompanies from "components/invite-project-companies";
import {
  QUERY_PROJECT_VENDORS,
  QUERY_USER_TYPE
} from "services/graphQL/queries";
import {
  getProjectVendorSources,
  isProjectInIntegrationMode
} from "utils/utils";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import CiqAgSearch from "components/ciq-ag-search";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import {
  dummyColumnDef,
  projectCompanyRoleRenderer,
  truncatedTextCellRenderer
} from "components/cell-renders";
import ProjectCompanyRoleSelectCellEditor from "components/cell-editor/project-company-role-cell-editor";
import { MUTATION_UPDATE_PROJECT_VENDOR } from "services/graphQL/mutations";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { useCIQQuery, useCIQLazyQuery } from "hooks/ciq-gql-hooks";
import { ErrorMessages, ProjectPermissionEnum } from "../../constants";

const useGetCompaniesData = () => {
  const { projectToken, eventLogs }: TProjectContext =
    useContext(ProjectContext);

  const [getCompaniesData] = useCIQLazyQuery(QUERY_PROJECT_VENDORS, {
    token: projectToken
  });
  const [companiesData, setCompaniesData] = useState<any>(null);
  useEffect(() => {
    const fetchCompaniesData = async () => {
      const res = await getCompaniesData();
      setCompaniesData(res?.data?.data);
    };
    if (projectToken) {
      fetchCompaniesData();
    }
  }, [getCompaniesData, projectToken]);

  useEffect(() => {
    const eventMatched = eventLogs?.some(
      (event: any) => event.data_source === "project_vendors"
    );
    if (eventMatched) {
      getCompaniesData().then((res: any) => {
        setCompaniesData(res?.data?.data);
      });
    }
  }, [eventLogs, getCompaniesData]);

  return companiesData;
};

function ProjectCompanies() {
  const gridRef = useRef<AgGridReact>(null);
  const [drawerCompany, setCompanyDrawerVisibility] = useState<boolean>(false);
  const [isGridReady, setGridReady] = useState(false);
  const {
    gqlClientForProject,
    tokenContents,
    projectDetails,
    isProjectArchived
  } = useContext(ProjectContext);
  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const { data: companyTypes } = useCIQQuery(QUERY_USER_TYPE);

  const companiesData = useGetCompaniesData();

  const [updateCompanyRole] = useMutation<any>(MUTATION_UPDATE_PROJECT_VENDOR, {
    client: gqlClientForProject
  });

  const isPermissionEditable = useCallback((params: any) => {
    const { context } = params;

    return !isPermissionNotGrantted(
      ProjectPermissionEnum.AddTradePartner,
      context?.projectRole ?? ""
    );
  }, []);

  const updateCompanyPermission = async (event: any) => {
    if (event.newValue === event.oldValue) return;

    const companyRole = companyTypes?.user_types?.find(
      (role: any) => role?.id === event.newValue
    );

    const oldNodeData = {
      ...event.data
    };

    event.node.setData({
      ...event.data,
      project_vendors_type: {
        name: companyRole?.name,
        id: companyRole?.id,
        key: companyRole?.key
      }
    });

    if (companyRole?.id) {
      const variables = {
        where: { vendor_id: { _eq: event?.data?.vendor_id } },
        _set: { type_id: companyRole.id }
      };

      try {
        const updateResponse = await updateCompanyRole({
          variables
        });
        if (updateResponse.data?.update_project_vendors?.affected_rows > 0) {
          message.success("Role of company updated successfully.");
        } else {
          message.error("Failed to update.");
          event.node.setData(oldNodeData);
        }
      } catch (ex) {
        console.error(ex);
        event.node.setData(oldNodeData);
        message.error("An error occured");
      }
    }
  };

  const cellEditRequest = async (event: any) => {
    if (!event.newValue || !isPermissionEditable(event)) return;
    if (event.column.colId === "project_vendors_type.name") {
      updateCompanyPermission(event);
    }
  };
  // Each Column Definition results in one Column.

  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        headerName: "Serial No.",
        valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
        headerTooltip: "Serial No.",
        width: 300,
        maxWidth: 300,
        sortable: false,
        menuTabs: [],
        editable: false,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        field: "subscription_vendor.name",
        headerName: "Company Name",
        headerTooltip: "Company Name",
        filter: false,
        sort: "asc",
        cellRenderer: truncatedTextCellRenderer,
        minWidth: 250
      },
      {
        field: "project_vendors_type.name",
        colId: "project_vendors_type.name",
        headerName: "Role of Company",
        headerTooltip: "Role of Company",
        filter: true,
        editable: (params) => {
          return isPermissionEditable(params);
        },
        cellRenderer: (params: any) => {
          return projectCompanyRoleRenderer(params);
        },
        cellEditor: ProjectCompanyRoleSelectCellEditor,
        cellClass: (params: any) => {
          return isPermissionEditable(params) ? "cell-editable" : "";
        },
        minWidth: 250
      },
      {
        colId: "external_source_id",
        headerName: "Source",
        hide: !isIntegrationMode,
        valueGetter: ({ data }) => {
          const allSources = getProjectVendorSources(data);
          return allSources.join(",");
        },
        cellRenderer: truncatedTextCellRenderer
      }
    ];
  }, [isIntegrationMode, isPermissionEditable]);

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      resizable: true,
      comparator: comparatorWithEmptyAtBottom,
      flex: 1
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    };
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const contextData = {
    companyTypes,
    projectRole: tokenContents?.role
  };

  const onFiltersApplied = () => {
    filterChipRef.current?.onFilterApplied();
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="ProjectCompany_search"
      gridRef={gridRef}
      placeholder="Search Project Companies"
    />,
    <FilterChipComponent
      key="ProjectCompany_FilterChip"
      gridRef={gridRef}
      ref={filterChipRef}
    />,
    <Button
      key="ProjectCompany_AddCompany"
      type="primary"
      onClick={() => {
        setCompanyDrawerVisibility(true);
      }}
      title={
        isProjectArchived
          ? undefined
          : isPermissionNotGrantted(
                ProjectPermissionEnum.AddTradePartner,
                tokenContents?.role ?? ""
              )
            ? ErrorMessages.PermissionNotGranted
            : "Add Company"
      }
      disabled={isPermissionNotGrantted(
        ProjectPermissionEnum.AddTradePartner,
        tokenContents?.role ?? ""
      )}
      data-testid="index-button-add-company"
    >
      Add Company
    </Button>
  ];

  return (
    <div
      className="w-full max-w-full flex flex-col"
      style={{ height: `calc(100vh - 140px - ${ARCHIVED_BANNER_HEIGHT})` }}
    >
      <CiqLogPageHeader
        {...{
          entityName: "Companies",
          titleParam: {
            title: "Companies",
            totalCount: companiesData?.project_vendors?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />
      <div className="grow ag-theme-alpine">
        <AgGridReact<any>
          ref={gridRef}
          onGridReady={() => {
            gridRef?.current?.api?.sizeColumnsToFit();
            setGridReady(true);
          }}
          rowData={companiesData?.project_vendors}
          columnDefs={columnDefs} // Column Defs for Columns
          defaultColDef={defaultColDef} // Default Column Properties
          animateRows={false} // Optional - set to 'true' to have rows animate when sorted
          loadingOverlayComponent={GridLoadingIndicator}
          suppressDragLeaveHidesColumns
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          tooltipShowMode="whenTruncated"
          context={contextData}
          onFilterChanged={onFiltersApplied}
          readOnlyEdit
          onCellEditRequest={cellEditRequest}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          suppressMovableColumns
        />
      </div>
      {drawerCompany && (
        <InviteProjectCompanies
          setDrawerVisibility={setCompanyDrawerVisibility}
          showAddProjectCompanyModel={drawerCompany}
        />
      )}
    </div>
  );
}

export default ProjectCompanies;
